import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Text,
} from '@chakra-ui/react';
import {
  FaArrowRight,
  FaClock,
  FaCoins,
  FaFire,
  FaMapMarkerAlt,
  FaRedo,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import {
  getBadgeColor,
  formatDateAbbr,
  getAvailableSeats,
  getButtonProps,
  getEventPriceRange,
  isSoldOut,
} from '../utils';
import { CIL_POINTS_LABEL } from '../utils/cilPoints';
import { eventAllowsPuntiPayment } from '../utils/eventPuntiPayment';

const MONTHS = [
  'Gen',
  'Feb',
  'Mar',
  'Apr',
  'Mag',
  'Giu',
  'Lug',
  'Ago',
  'Set',
  'Ott',
  'Nov',
  'Dic',
];

/** Flag sull'immagine: sfondo scuro fisso, solo il testo è colorato. */
const Flag = ({ color, icon, children }) => (
  <HStack
    spacing={1.5}
    h="24px"
    px={2.5}
    borderRadius="6px"
    bg="rgba(22,24,29,0.88)"
    backdropFilter="blur(6px)"
    color={color}
    fontSize="11px"
    fontWeight="800"
    letterSpacing="0.06em"
    textTransform="uppercase"
  >
    {icon && <Icon as={icon} boxSize="12px" />}
    <span>{children}</span>
  </HStack>
);

const EventCard = ({
  event,
  withMargin,
  hideButton = false,
  showDescription = false,
  index,
}) => {
  const navigate = useNavigate();

  const availableSeats = getAvailableSeats(event);
  const { text } = getButtonProps(event, availableSeats);

  const soldOut = isSoldOut(event);
  const hasRewards =
    Array.isArray(event.reward_sets) && event.reward_sets.length > 0;
  const showCilPointsBadge = hasRewards || eventAllowsPuntiPayment(event);
  const isHot =
    !soldOut &&
    (event.isFeatured ||
      (event.capienza && availableSeats < event.capienza * 0.51));

  const accent = event.color || '#ffcc00';
  const startDate = event.Data ? new Date(event.Data) : null;
  const hasStartDate = startDate && !Number.isNaN(startDate.getTime());
  const time = event.Ora_inizio
    ? event.Ora_inizio.split(':').slice(0, 2).join(':')
    : null;

  const dateLabel = event.Recurring
    ? `Ogni ${event.Recurring}`
    : event.Date_end
      ? `${formatDateAbbr(event.Data, { withYear: false })} – ${formatDateAbbr(event.Date_end, { withYear: false })}`
      : null;
  const whenLabel = [dateLabel, time].filter(Boolean).join(' · ');

  const isFree = Boolean(event.is_free);
  const clickable = !hideButton;
  const goTo = () => navigate(`/event/${event.urlTitle}`);

  return (
    <Box
      key={event.documentId}
      bg="#1e2127"
      color="white"
      border="1px solid"
      borderColor="rgba(255,255,255,0.08)"
      borderRadius="14px"
      overflow="hidden"
      position="relative"
      display="flex"
      flexDirection="column"
      cursor={clickable ? 'pointer' : 'default'}
      onClick={clickable ? goTo : undefined}
      role={clickable ? 'link' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter') goTo();
            }
          : undefined
      }
      transition="transform 0.25s, border-color 0.25s, box-shadow 0.25s"
      _hover={
        clickable
          ? {
              transform: 'translateY(-4px)',
              borderColor: accent,
              boxShadow: '0 14px 32px rgba(0,0,0,0.35)',
            }
          : {}
      }
      _active={
        clickable ? { transform: 'scale(0.98)', transition: 'none' } : {}
      }
      sx={{
        '&:hover .ev-go': { color: accent },
        '&:hover .ev-go svg': { transform: 'translateX(3px)' },
      }}
      w="100%"
      maxW={hideButton ? { base: 'unset', lg: '550px' } : 'unset'}
      minW={0}
      flex={1}
      mr={withMargin ? 4 : 0}
      opacity={soldOut ? 0.85 : 1}
      data-testid={
        typeof index === 'number' ? `event-card-${index}` : undefined
      }
    >
      {/* Immagine */}
      <Box
        position="relative"
        overflow="hidden"
        sx={{ aspectRatio: '16 / 10' }}
        bg={`linear-gradient(160deg, ${accent}99 0%, #1b1e24 100%)`}
      >
        <Image
          src={
            event.thumbnail?.formats?.medium?.url ||
            event.thumbnail?.url ||
            '/default-image-url.png'
          }
          alt={event.Nome}
          position="absolute"
          inset={0}
          w="100%"
          h="100%"
          objectFit="cover"
          filter={soldOut ? 'grayscale(0.7)' : undefined}
        />
        <Box
          position="absolute"
          inset={0}
          bg="linear-gradient(180deg, transparent 55%, rgba(0,0,0,0.45))"
          pointerEvents="none"
        />

        {/* Tile data */}
        {hasStartDate && (
          <Box
            position="absolute"
            top={4}
            left={4}
            w="56px"
            pt="6px"
            pb="7px"
            textAlign="center"
            lineHeight={1}
            bg="rgba(22,24,29,0.88)"
            backdropFilter="blur(6px)"
            borderRadius="10px"
            borderTop="3px solid"
            borderTopColor={accent}
          >
            <Text fontSize="22px" fontWeight="800" lineHeight={1}>
              {String(startDate.getDate()).padStart(2, '0')}
            </Text>
            <Text
              mt="3px"
              fontSize="11px"
              fontWeight="700"
              letterSpacing="0.1em"
              textTransform="uppercase"
              color="gray.400"
              lineHeight={1}
            >
              {MONTHS[startDate.getMonth()]}
            </Text>
          </Box>
        )}

        {/* Flag di stato */}
        {(soldOut || isHot || showCilPointsBadge || event.isRecurring) && (
          <Flex
            position="absolute"
            top={4}
            right={4}
            direction="column"
            align="flex-end"
            gap={1.5}
          >
            {soldOut && <Flag color="#e73341">Posti esauriti</Flag>}
            {isHot && (
              <Flag color="#ff7a59" icon={FaFire}>
                Hot
              </Flag>
            )}
            {showCilPointsBadge && (
              <Flag color="#ffcc00" icon={FaCoins}>
                {CIL_POINTS_LABEL}
              </Flag>
            )}
            {event.isRecurring && (
              <Flag color="#a98bff" icon={FaRedo}>
                Ricorrente
              </Flag>
            )}
          </Flex>
        )}
      </Box>

      {/* Corpo */}
      <Flex direction="column" gap={2} p={6} pb={4} flex={1}>
        {event.Partecipanti && (
          <HStack
            spacing={2}
            fontSize="12px"
            fontWeight="700"
            letterSpacing="0.06em"
            textTransform="uppercase"
            color="gray.400"
          >
            <Box
              w="7px"
              h="7px"
              borderRadius="full"
              bg={getBadgeColor(event.Partecipanti)}
            />
            <span>{event.Partecipanti}</span>
          </HStack>
        )}
        <Heading
          as="h3"
          fontSize="19px"
          fontWeight="800"
          lineHeight="1.25"
          letterSpacing="-0.02em"
          noOfLines={2}
        >
          {event.Nome}
        </Heading>
        {showDescription && event.Descrizione && (
          <Text fontSize="14px" lineHeight="1.5" color="gray.300" noOfLines={3}>
            {event.Descrizione}
          </Text>
        )}
        <Flex
          direction="column"
          gap={1}
          mt={1}
          fontSize="14px"
          color="gray.400"
        >
          {whenLabel && (
            <HStack spacing={2}>
              <Icon as={FaClock} boxSize="13px" color="gray.500" />
              <span>{whenLabel}</span>
            </HStack>
          )}
          {event.Location && (
            <HStack spacing={2}>
              <Icon as={FaMapMarkerAlt} boxSize="13px" color="gray.500" />
              <Text noOfLines={1}>{event.Location}</Text>
            </HStack>
          )}
        </Flex>
      </Flex>

      {/* Footer: prezzo + azione testuale (l'intera card è il link) */}
      <Flex
        align="center"
        justify="space-between"
        px={6}
        py={4}
        borderTop="1px solid"
        borderColor="rgba(255,255,255,0.08)"
      >
        <Text
          fontSize="17px"
          fontWeight="800"
          color={isFree ? '#3eb297' : 'white'}
        >
          {getEventPriceRange(event)}
        </Text>
        {clickable && event?.Varianti?.length > 0 && (
          <HStack
            className="ev-go"
            spacing={1.5}
            fontSize="14px"
            fontWeight="700"
            color="gray.400"
            transition="color 0.2s"
          >
            <span>{text.replace(/!$/, '')}</span>
            <Icon
              as={FaArrowRight}
              boxSize="12px"
              transition="transform 0.2s"
            />
          </HStack>
        )}
      </Flex>
    </Box>
  );
};

export default EventCard;
